import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { fmtMoney } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export const MergeDialog = ({ open, onOpenChange, customer, customerId, onMerged }) => {
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [target, setTarget] = useState(null);

  useEffect(() => {
    if (!open || !search) { setResults([]); return; }
    const t = setTimeout(async () => {
      const { data } = await api.get("/customers", { params: { q: search, limit: 5 } });
      setResults(data.items.filter((c) => c.id !== customerId));
    }, 250);
    return () => clearTimeout(t);
  }, [search, open, customerId]);

  const close = (v) => {
    onOpenChange(v);
    if (!v) { setTarget(null); setSearch(""); }
  };

  const doMerge = async () => {
    try {
      await api.post("/customers/merge", { primary_id: customerId, duplicate_id: target.id });
      toast.success("Records merged into this customer.");
      close(false);
      onMerged();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-serif text-2xl">Merge another record into this one</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">
          The other customer's orders will move to <strong>{customer.first_name} {customer.last_name}</strong>, and the other record will be removed.
        </p>
        <Input placeholder="Search for the duplicate record…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="merge-search-input" />
        <div className="space-y-2">
          {results.map((c) => (
            <button
              key={c.id}
              onClick={() => setTarget(c)}
              data-testid={`merge-result-${c.id}`}
              className={`w-full text-left rounded-md border p-3 text-sm transition-colors duration-200 ${target?.id === c.id ? "border-primary bg-secondary" : "border-border hover:bg-secondary/50"}`}
            >
              <p className="font-medium">{c.first_name} {c.last_name}</p>
              <p className="text-xs text-muted-foreground">{c.email || "no email"} · {c.order_count} orders · {fmtMoney(c.lifetime_spend)}</p>
            </button>
          ))}
        </div>
        <DialogFooter>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button disabled={!target} data-testid="merge-confirm-open-btn">Merge records</Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle className="font-serif">Merge {target?.first_name} {target?.last_name} into {customer.first_name} {customer.last_name}?</AlertDialogTitle>
                <AlertDialogDescription>All of their orders will be combined. This cannot be undone.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Go back</AlertDialogCancel>
                <AlertDialogAction onClick={doMerge} data-testid="confirm-merge-btn">Yes, merge them</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
